import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Icon,
  Tag,
  Tooltip,
  useDisclosure,
  Image,
} from '@chakra-ui/react';
import { TAdvancedColumnData, TTab } from '../tabs/type';
import { CirlceIcon, NormalIcon, allIcons } from '../utils/Icon';
import { updateTabOrder } from '../../api/tabsApi';
import { TState, useActions } from '../../redux/store';
import DragDropList from '../dragAndDrop/DragDropList';
import Chip from '../common/Chip';
import { useAllTabs } from '../../redux/actions';
import Logo from './Logo';

type TMenuProps = {
  tab: Partial<TTab>;
  isSelected: boolean;
  isSiblingsSelected?: boolean;
  pageName?: string;
  onClick?: any;
  isOpen?: boolean;
  isCustom?: boolean;
  onToggle?: () => void;
};

const MenuItem = ({
  tab: {
    _id: tabId,
    title,
    icon,
    isMenu,
    menuChilds,
    cloneId,
    isAuthOnlyChild,
  },
  isSelected,
  onClick,
  isOpen,
  isCustom,
  isSiblingsSelected,
  pageName,
  onToggle,
}: TMenuProps) => {
  const isEditMode = useSelector((state: TState) => state.isEditMode);
  const dataCount = useSelector((state: TState) => state.dataCount);
  const isNavOpen = useSelector((state: TState) => state.isHovered);
  const theme = useSelector((state: TState) => state.theme);

  const { setModalType } = useActions();

  const [isTruncated, setIsTrucated] = useState(false);
  const ref = useRef<any>();

  const isHovered = isNavOpen || isEditMode;
  const hasCount = dataCount[`${tabId}`] > 0;

  useEffect(() => {
    setTimeout(() => {
      setIsTrucated(
        ref.current && ref.current?.scrollWidth > ref.current?.clientWidth,
      );
    }, 301);

    localStorage.setItem('isMenuOpen', isHovered ? 'true' : 'false');
  }, [isHovered, ref.current?.clientWidth]);

  return (
    <Flex
      position='relative'
      w='100%'
      px={4}
      py={!isHovered ? 3 : isMenu ? 0 : 1}
      pl={isHovered && isMenu ? 8 : 3}
      margin={isHovered ? 'none' : 'auto'}
      cursor='pointer'
      alignItems='center'
      color={isSelected ? theme.none : theme.main}
      bgColor={
        isSelected
          ? theme.dark
          : isSiblingsSelected
            ? theme.lighter
            : theme.navBg
      }
      borderRight={'2px solid transparent'}
      borderRightColor={isSelected || isSiblingsSelected ? theme.dark : 'none'}
      fontWeight={isSelected ? '500' : '0'}
      onClick={onClick}
    >
      <Icon
        as={allIcons[icon || 'HiFolder']}
        boxSize={theme.fontSize[isHovered ? 'big' : 'header']}
        color={
          isSelected
            ? theme.none
            : theme.main /*theme[isSelected ? 'lighter' : 'none']*/
        }
      />
      {isHovered && (
        <Box
          ref={ref}
          p={1}
          px={2}
          fontSize={isMenu ? theme.fontSize.small : theme.fontSize.normal}
          textAlign='center'
          textTransform='capitalize'
          whiteSpace='nowrap'
          textOverflow='ellipsis'
          overflow='hidden'
        >
          {title}
        </Box>
      )}
      {!isAuthOnlyChild && hasCount && (
        <Chip isBig={isHovered} nb={`${dataCount[`${tabId}`]}`} />
      )}
      {(!isHovered || isTruncated) && (
        <Tooltip
          label={pageName || title}
          placement='right'
          textTransform='capitalize'
        >
          <Box position='absolute' w='100%' h='100%' top={0} left={0} />
        </Tooltip>
      )}
      <Box flexGrow={1} />
      {!isEditMode && !!menuChilds?.length && isHovered && (
        <NormalIcon
          color={isSelected ? theme.none : theme.main}
          as={isOpen ? allIcons.HiChevronUp : allIcons.HiChevronDown}
          boxSize={15}
          onClick={onToggle}
          fontStyle='bold'
        />
      )}
      {!isCustom && isEditMode && (
        <Flex ml={3} zIndex={1}>
          {cloneId ? (
            <Tag mr={1}>Clone</Tag>
          ) : (
            <CirlceIcon
              boxSize='27px'
              as={allIcons.HiMiniDocumentDuplicate}
              onClick={() => {
                setModalType('duplicateTab');
              }}
            />
          )}
          {!isMenu && !cloneId && (
            <CirlceIcon
              boxSize='27px'
              as={allIcons.HiPlusSmall}
              onClick={() => {
                setModalType('addMenuSubTab');
              }}
            />
          )}
          <CirlceIcon
            boxSize='27px'
            as={allIcons.HiPencilSquare}
            onClick={() => {
              setModalType(isMenu ? 'editMenuSubTab' : 'editTab');
            }}
          />
          <CirlceIcon
            boxSize='27px'
            as={allIcons.HiMiniXMark}
            onClick={() => {
              setModalType('delTab');
            }}
          />
        </Flex>
      )}
    </Flex>
  );
};

const useGetTitle = () => {
  const tabs = useAllTabs();

  return (tab: TTab) => {
    const parentTitle = tabs[tab?.parent_id || '']?.title || '';
    const tabTitle = tab?.title || '';
    return `${tab?.parent_id ? `${parentTitle} / ` : ''}${tabTitle}`;
  };
};

const Elements = ({ tab, hideChilds }: { tab: TTab; hideChilds?: boolean }) => {
  const isEditMode = useSelector((state: TState) => state.isEditMode);
  const currentTab = useSelector((state: TState) => state.currentTab);
  const isMobile = useSelector((state: TState) => state.isMobile);
  const theme = useSelector((state: TState) => state.theme);

  const {
    setCurrentTab,
    setTabToUpdate,
    getTabs,
    onToggle: onClose,
  } = useActions();

  const { onOpen, onToggle } = useDisclosure({ isOpen: false });
  const tabs = useAllTabs();
  const getTitle = useGetTitle();

  const keepOpen =
    tab._id === currentTab?._id ||
    !!tab.menuChilds?.find((mc) => mc._id === currentTab?._id);

  const handleChange = (tab: TTab) => {
    const isSame = currentTab?._id === tab._id;

    const isAuthOnlyChild = tab.isAuthOnlyChild;
    setCurrentTab(isAuthOnlyChild ? tab.menuChilds?.[0] : tab);
    setTabToUpdate(isAuthOnlyChild ? tab.menuChilds?.[0] : tab);

    const closeMenu = isMobile && (!tab.menuChilds?.length || isSame);
    closeMenu && onClose();
  };

  const onClick = useCallback(() => handleChange(tab), [tab]);

  const handleOrderChange = useCallback(async (tabsOrdered: TTab[]) => {
    await updateTabOrder(tabsOrdered.map((t) => t._id));
    getTabs();
  }, []);

  if (!isEditMode) {
    return tab.menuChilds?.length ? (
      <Box position='relative' bg={theme.navBg}>
        <Box p={0} onClick={onOpen}>
          <MenuItem
            key={tab._id}
            tab={tab}
            isSelected={tab._id === currentTab?._id}
            onClick={() => handleChange(tab)}
            isOpen={keepOpen}
            onToggle={onToggle}
            isSiblingsSelected={tab._id === currentTab?.parent_id}
            pageName={getTitle(tab)}
          />
        </Box>
        <Collapse in={keepOpen} animateOpacity>
          <Box p={0}>
            {tab.menuChilds?.map((menuChild) => (
              <MenuItem
                key={menuChild._id}
                tab={menuChild}
                isSelected={menuChild._id === currentTab?._id}
                onClick={() => handleChange(menuChild)}
                isSiblingsSelected={
                  menuChild.parent_id === currentTab?._id ||
                  menuChild.parent_id === tabs[`${currentTab?._id}`]?.parent_id
                }
                pageName={getTitle(menuChild)}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    ) : (
      <Box bg={theme.navBg}>
        <MenuItem
          key={tab._id}
          tab={tab}
          isSelected={tab._id === currentTab?._id}
          onClick={() => handleChange(tab)}
          pageName={getTitle(tab)}
        />
      </Box>
    );
  }

  return (
    <Flex flexDirection='column' w='100%' py={1}>
      <MenuItem
        key={tab._id}
        tab={tab}
        isSelected={tab._id === currentTab?._id}
        onClick={onClick}
        pageName={getTitle(tab)}
      />
      {hideChilds ? null : isEditMode ? (
        <DragDropList
          onDrop={handleOrderChange}
          listItem={tab.menuChilds || []}
          List={({ item }) => <Elements tab={item} />}
          type='SUBMENU'
        />
      ) : (
        tab.menuChilds?.map((menuChild) => <Elements tab={menuChild} />)
      )}
    </Flex>
  );
};

const Navbar = () => {
  const isEditMode = useSelector((state: TState) => state.isEditMode);
  const isNavOpen = useSelector((state: TState) => state.isHovered);
  const isPlanning = useSelector((state: TState) => state.isPlanning);
  const isDuplicate = useSelector((state: TState) => state.isDuplicate);
  const theme = useSelector((state: TState) => state.theme);
  const tabs = useSelector((state: TState) => state.tabs);
  const settingsTabs = useSelector((state: TState) => state.settingsTabs);
  const duplicateTab = useSelector((state: TState) => state.duplicateTab);
  const planningTab = useSelector((state: TState) => state.planningTab);
  const currentTab = useSelector((state: TState) => state.currentTab);
  const isMobile = useSelector((state: TState) => state.isMobile);
  const isAdmin = useSelector((state: TState) => state.isAdmin);

  const { onToggle, setModalType, setCurrentTab, getTabs } = useActions();

  const isHovered = isNavOpen || isEditMode;

  const handleChange = async (tab: TTab) => {
    setCurrentTab(tab);

    const closeMenu = isMobile && !tab.menuChilds?.length;
    closeMenu && onToggle();
  };

  const handleOrderChange = async (tabsOrdered: TTab[]) => {
    await updateTabOrder(tabsOrdered.map((t) => t._id));
    getTabs();
  };

  const { isOpen: canDrag, onToggle: toggleCanDrag } = useDisclosure();

  const isEvisionLogo = /evisioncrm|localhost/.test(window.location.hostname);
  const logoUrl = `logo${localStorage.getItem('company')}${
    isHovered ? 'big' : ''
  }.png`;

  return (
    <Flex
      id='navbar'
      className='container'
      direction='column'
      position={['absolute', 'relative']}
      zIndex={4}
      w={isHovered ? ['100%', isEditMode ? '400px' : '320px'] : '48px'}
      h={isHovered ? ['100%', 'auto'] : 'auto'}
      visibility={isHovered ? 'visible' : ['hidden', 'visible']}
      opacity={isHovered ? '1' : ['0', '1']}
      bg={theme.navBg}
      transition={['opacity 0.3s ease, width 0.3s ease', 'all 0.3s ease']}
      overflowX='hidden'
      boxShadow='xl'
    >
      <Flex alignItems='center' position='relative'>
        {isMobile && isHovered && (
          <NormalIcon
            as={allIcons.HiBars3}
            onClick={onToggle}
            position='absolute'
            top={5}
            left={3}
          />
        )}
        <Box margin='auto' overflow='hidden' flexShrink={0} my={3} p={1}>
          {isEvisionLogo ? (
            <Logo big={isHovered} />
          ) : (
            <Image src={logoUrl} h={10} />
          )}
        </Box>
      </Flex>
      <Divider borderBottomWidth={1} borderColor='gray.400' />
      {isEditMode && (
        <Button onClick={toggleCanDrag}>Afficher/Cacher les sous-menu</Button>
      )}
      <Box overflow='auto' flexGrow={1} mt={isEditMode ? 3 : 0}>
        {isEditMode && canDrag ? (
          <DragDropList
            onDrop={handleOrderChange}
            List={({ item }) => <Elements tab={item} hideChilds={true} />}
            listItem={tabs}
            type='MENU'
          />
        ) : (
          tabs.map((tab) => <Elements tab={tab} />)
        )}
        {isEditMode && (
          <CirlceIcon
            as={allIcons.HiPlusSmall}
            m={3}
            onClick={() => {
              setModalType('addTab');
            }}
          />
        )}
      </Box>
      <Divider borderBottomWidth={1} borderColor='gray.400' />
      <Box fontSize={theme.fontSize.normal}>
        {!isEditMode && !!settingsTabs.length && isAdmin && (
          <MenuItem
            tab={{
              title: 'Stats',
              icon: 'HiChartBar',
            }}
            isSelected={!!currentTab?.isStats}
            onClick={() =>
              handleChange({
                _id: 'stats',
                title: 'Stats',
                tableColumns: [] as TAdvancedColumnData[],
                isStats: true,
              } as TTab)
            }
          />
        )}
        {isPlanning && (
          <MenuItem
            tab={{
              title: 'planning',
              icon: 'HiCalendarDays',
            }}
            isSelected={!!currentTab?.isPlanning}
            onClick={() => {
              handleChange(planningTab);
            }}
            isCustom
          />
        )}
        {isDuplicate && (
          <MenuItem
            tab={{
              ...duplicateTab,
              title: 'doublons',
              icon: 'HiDocumentDuplicate',
            }}
            isSelected={!!currentTab?.isDuplicate}
            onClick={() => {
              handleChange(duplicateTab);
            }}
            isCustom
          />
        )}
        {!isEditMode && !!settingsTabs.length && (
          <MenuItem
            tab={{
              title: 'paramètres',
              icon: 'HiCog6Tooth',
            }}
            isSelected={!!currentTab?.isSetting}
            onClick={() =>
              handleChange({
                _id: 'settings',
                title: 'Paramètres',
                isSetting: true,
                tableColumns: [] as TAdvancedColumnData[],
              } as TTab)
            }
          />
        )}
        {/* {!isEditMode && (
          <NormalIcon
            // color='#fff'
            as={allIcons[`HiArrow${isHovered ? 'Left' : 'Right'}Circle`]}
            onClick={() => onToggle()}
            margin='auto'
            my={3}
          />
        )} */}
      </Box>
    </Flex>
  );
};

export default Navbar;
